import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'

import { usePageTitle } from '../../hooks/usePageTitle'
import { Item } from '../../models/Item'
import { RecordFactory } from '../../models/RecordFactory'
import { useOnboardingContext } from '../../providers/OnboardingProvider'
import { okResult } from '../../types/utils'
import { editionDefaultsForType } from '../../utils/onboarding'
import { getItemTypeFromPath } from '../../utils/onboarding'
import { getEditionID } from '../../utils/users'
import { PSApplication } from '../app/PSApplication'
import { ItemsPage, itemTitleAndHelperText } from '../items/ItemsPage'
import { WorkflowSelectListSkeleton } from '../common/skeletons/WorkflowSelectListSkeleton'
import { useOnboardingBackHandler } from './useOnboardingBackHandler'

// helper function to display edition default categories while allowing user edits to show
const mergeItems = (defaults: Item[], saved: Item[]) => {
    return defaults.map((d) => {
        const foundFromSaved = saved.find((c) => c.editionItem === d.id)
        if (foundFromSaved) {
            return { item: foundFromSaved, selected: true }
        }

        return { item: d, selected: false }
    })
}

export function SetupOnboardingItemsPage() {
    const me = PSApplication.me()
    const owner = PSApplication.owner()
    const editionID = getEditionID(me)
    const [searchParams] = useSearchParams()
    const location = useLocation()
    const { advanceOnboardingState } = useOnboardingContext()

    const itemType = getItemTypeFromPath(location.pathname)
    const { title, helperText } = itemTitleAndHelperText(itemType)
    const navigate = useNavigate()

    const onBack = useOnboardingBackHandler()

    usePageTitle(title)

    const { data, isLoading } = useQuery({
        queryKey: ['onboarding', location.pathname, editionID],
        queryFn: async () => {
            const [edition, installedItems] = await Promise.all([
                RecordFactory.getInstance().loadEdition(getEditionID(me)),
                RecordFactory.getInstance().loadItems(owner.id!),
            ])

            const defaultItems = editionDefaultsForType(itemType, edition)

            const recordIDs = searchParams.get('recordIDs')?.split(',') ?? []

            const itemsToSelect =
                recordIDs.length && itemType !== 'product'
                    ? defaultItems
                          .filter((i) => recordIDs.includes(i.id ?? ''))
                          .map((i) => ({ item: i, selected: true }))
                    : mergeItems(defaultItems, installedItems)

            return {
                edition,
                itemsToSelect,
            }
        },
    })

    const onNextHandler = useCallback(
        async (items: Item[]) => {
            // Send the new items with IDs cleared and edition information set
            const clonedItems = items.map((item) => {
                const clone = item.clone()
                clone.edition = editionID
                clone.editionItem = item.id
                clone.id = undefined
                return clone
            })

            const result = await RecordFactory.getInstance().sendOnboardingItemsSetup(
                itemType,
                clonedItems,
                editionID,
                true
            )

            if (!result.ok) {
                return result
            }

            const potentialItemIDs = searchParams.get('recordIDs')?.split(',') ?? []
            const potentialFormsAndContracts = items
                .flatMap((i) => [i.onboardingFormID, i.onboardingContractID])
                .filter(Boolean) as string[]

            await advanceOnboardingState([...potentialItemIDs, ...potentialFormsAndContracts])

            return okResult(null)
        },
        [advanceOnboardingState, editionID, searchParams, itemType]
    )

    if (isLoading || !data) {
        return <WorkflowSelectListSkeleton title={title} subtitle={helperText} onClose={() => {}} />
    }

    return (
        <ItemsPage
            edition={data.edition}
            items={data.itemsToSelect}
            itemType={itemType}
            onSave={onNextHandler}
            enableAllFields={false}
            onClose={() => navigate('/')}
            onBack={onBack}
        />
    )
}
